import { useState, useRef, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/errors";
import { Phone, ArrowLeft, Mail } from "lucide-react";

export default function PhoneLogin() {
  const { user, loading, sendOtp, verifyOtp, completeOtpRegistration } = useAuth();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1 = phone, 2 = OTP, 3 = register
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [pendingToken, setPendingToken] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regName, setRegName] = useState("");
  const otpRef = useRef(null);
  const emailRef = useRef(null);

  useEffect(() => {
    if (step === 2 && otpRef.current) otpRef.current.focus();
    if (step === 3 && emailRef.current) emailRef.current.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (loading) return <div className="py-24 text-center text-stone-500">Loading…</div>;
  if (user) return <Navigate to={user.is_admin ? "/admin" : "/account"} replace />;

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await sendOtp(phone);
      setStep(2);
      setCooldown(30);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await verifyOtp(phone, otp);
      if (u && u.needs_registration) {
        setPendingToken(u.pending_token);
        setStep(3);
      } else {
        window.location.href = u.is_admin ? "/admin" : "/account";
      }
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const completeRegistration = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await completeOtpRegistration(pendingToken, regEmail, regName);
      window.location.href = u.is_admin ? "/admin" : "/account";
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const heading = step === 1 ? "Enter your mobile"
                : step === 2 ? "Enter the OTP"
                : "Complete your profile";
  const subheading = step === 1 ? "We'll text you a 6-digit code."
                   : step === 2 ? `Sent to +91 ${phone}`
                   : "A few quick details and you're in.";

  return (
    <div className="relative min-h-[70vh]">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 40, right: -180 }}/>
      <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: 100, left: -140 }}/>
      <div className="max-w-md mx-auto px-6 py-16 relative">
        <div className="text-center mb-8">
          <div className="uppercase tracking-[0.3em] text-xs text-[#8B7355] mb-3 flex items-center justify-center gap-1.5">
            {step === 3 ? <Mail className="w-3 h-3"/> : <Phone className="w-3 h-3"/>}
            {step === 3 ? "Finish signup" : "One-tap login"}
          </div>
          <h1 className="font-serif-display text-5xl text-[#3B2F2F]">{heading}</h1>
          <p className="text-sm text-stone-500 mt-2">{subheading}</p>
        </div>

        <form onSubmit={step === 1 ? sendCode : step === 2 ? verify : completeRegistration}
              className="bg-white/80 backdrop-blur rounded-3xl border border-stone-200 shadow-xl p-8 space-y-4"
              data-testid="phone-login-form">
          {step === 1 && (
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Mobile number</label>
              <div className="mt-1 flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-full px-4 py-1 focus-within:border-[#3B2F2F]">
                <span className="text-sm text-stone-500">+91</span>
                <input type="tel" required maxLength={10} value={phone}
                       onChange={(e)=>setPhone(e.target.value.replace(/\D/g,"").slice(0,10))}
                       placeholder="9876543210" data-testid="phone-input"
                       className="flex-1 bg-transparent py-2 outline-none tracking-wider"/>
              </div>
            </div>
          )}

          {step === 2 && (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">6-digit OTP</label>
                <input ref={otpRef} type="text" inputMode="numeric" required maxLength={6}
                       value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))}
                       placeholder="••••••" data-testid="otp-input"
                       className="mt-1 w-full text-center tracking-[0.5em] font-mono text-2xl bg-stone-50 border border-stone-200 rounded-full px-4 py-3 outline-none focus:border-[#3B2F2F]"/>
              </div>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={()=>{ setStep(1); setOtp(""); setErr(""); }}
                        className="text-[#8B7355] hover:text-[#3B2F2F] flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3"/> Change number
                </button>
                <button type="button" disabled={cooldown > 0 || busy} onClick={sendCode}
                        data-testid="resend-otp"
                        className="text-[#8B7355] hover:text-[#3B2F2F] disabled:opacity-40 disabled:cursor-not-allowed">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">Full name</label>
                <input type="text" value={regName} onChange={(e)=>setRegName(e.target.value)}
                       placeholder="Your name" data-testid="otp-reg-name"
                       className="mt-1 w-full bg-stone-50 border border-stone-200 rounded-full px-4 py-3 outline-none focus:border-[#3B2F2F]"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">Email <span className="text-red-500">*</span></label>
                <input ref={emailRef} type="email" required value={regEmail}
                       onChange={(e)=>setRegEmail(e.target.value)}
                       placeholder="you@example.com" data-testid="otp-reg-email"
                       className="mt-1 w-full bg-stone-50 border border-stone-200 rounded-full px-4 py-3 outline-none focus:border-[#3B2F2F]"/>
                <p className="text-[11px] text-stone-500 mt-1.5">We'll send order updates &amp; invoices here.</p>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="text-stone-500">Mobile <span className="font-medium text-stone-700">+91 {phone}</span></div>
                <button type="button" onClick={()=>{ setStep(1); setOtp(""); setPendingToken(""); setErr(""); }}
                        className="text-[#8B7355] hover:text-[#3B2F2F] flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3"/> Start over
                </button>
              </div>
            </>
          )}

          {err && <div data-testid="phone-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{err}</div>}

          <button type="submit"
                  disabled={busy
                    || (step===1 && phone.length!==10)
                    || (step===2 && otp.length!==6)
                    || (step===3 && !regEmail)}
                  data-testid={step === 1 ? "send-otp-btn" : step === 2 ? "verify-otp-btn" : "otp-reg-submit"}
                  className="w-full bg-[#3B2F2F] text-[#FDFBF7] rounded-full py-3 font-semibold hover:bg-black disabled:opacity-60">
            {busy
              ? (step === 1 ? "Sending…" : step === 2 ? "Verifying…" : "Creating account…")
              : (step === 1 ? "Send OTP" : step === 2 ? "Verify & continue" : "Create account & sign in")}
          </button>

          {step !== 3 && (
            <Link to="/login" className="block text-center text-xs text-[#8B7355] hover:text-[#3B2F2F]">← Prefer email &amp; password</Link>
          )}
        </form>
      </div>
    </div>
  );
}
